/**
 * Professional Invoice Generator
 * Generates a branded HTML invoice and opens it in a new window for printing/saving as PDF.
 */

const formatCurrency = (amount, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(amount / 100);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getInvoiceNumber = (payment) => {
  const date = new Date(payment.created_at);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const id = String(payment.id).padStart(4, '0');
  return `INV-${year}${month}-${id}`;
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'succeeded': return { label: '✓ PAID', color: '#276749', bg: '#c6f6d5' };
    case 'pending':   return { label: '⏳ PENDING', color: '#744210', bg: '#fefcbf' };
    case 'failed':    return { label: '✗ FAILED', color: '#742a2a', bg: '#fed7d7' };
    default:          return { label: status.toUpperCase(), color: '#4a5568', bg: '#e2e8f0' };
  }
};

/**
 * Generate and open a professional invoice in a new browser window.
 *
 * @param {Object} payment     - Payment record from Redux state
 * @param {Object} user        - Authenticated user from Redux auth state
 * @param {Object} subscription - Current subscription from Redux state
 */
export const generateInvoice = (payment, user, subscription) => {
  const invoiceNumber = getInvoiceNumber(payment);
  const statusInfo = getStatusLabel(payment.status);
  const planName = payment.plan_name || subscription?.plan?.name || 'Streaming Plan';
  const description = payment.description || `${planName} - Subscription Payment`;
  const amount = formatCurrency(payment.amount, payment.currency);
  const invoiceDate = formatDate(payment.created_at);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Invoice ${invoiceNumber} — Teve</title>
  <style>
    /* ── Reset ── */
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    /* ── Base ── */
    body {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      color: #2d3748;
      background: #f7fafc;
      padding: 30px 20px;
    }

    /* ── Print button (hidden when printing) ── */
    .print-actions {
      max-width: 860px;
      margin: 0 auto 20px;
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }
    .btn-print {
      padding: 10px 24px;
      background: #6c5ce7;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background 0.2s;
    }
    .btn-print:hover { background: #5a4bd1; }
    .btn-close-win {
      padding: 10px 24px;
      background: #e2e8f0;
      color: #4a5568;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }
    .btn-close-win:hover { background: #cbd5e0; }

    /* ── Invoice card ── */
    .invoice {
      max-width: 860px;
      margin: 0 auto;
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.08);
      overflow: hidden;
    }

    /* ── Header band ── */
    .invoice-header {
      background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
      padding: 40px 48px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .brand-block .brand-name {
      font-size: 36px;
      font-weight: 900;
      color: #fff;
      letter-spacing: -1px;
      line-height: 1;
    }
    .brand-block .brand-tagline {
      font-size: 13px;
      color: rgba(255,255,255,0.75);
      margin-top: 6px;
      letter-spacing: 0.5px;
    }
    .invoice-title-block { text-align: right; }
    .invoice-title-block .invoice-word {
      font-size: 42px;
      font-weight: 800;
      color: rgba(255,255,255,0.95);
      letter-spacing: -1px;
      line-height: 1;
    }
    .invoice-title-block .invoice-num {
      font-size: 15px;
      color: rgba(255,255,255,0.8);
      margin-top: 8px;
      font-weight: 500;
    }
    .invoice-title-block .invoice-date {
      font-size: 13px;
      color: rgba(255,255,255,0.65);
      margin-top: 4px;
    }

    /* ── Status strip ── */
    .status-strip {
      padding: 16px 48px;
      background: #fafafa;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 18px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    /* ── Body ── */
    .invoice-body { padding: 40px 48px; }

    /* ── Info grid ── */
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-bottom: 40px;
    }
    .info-block h4 {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #6c5ce7;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 2px solid #e9e4ff;
    }
    .info-block .name {
      font-size: 17px;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 4px;
    }
    .info-block p {
      font-size: 14px;
      color: #718096;
      line-height: 1.7;
      margin: 0;
    }

    /* ── Items table ── */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 32px;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    }
    .items-table thead tr {
      background: linear-gradient(90deg, #6c5ce7, #a29bfe);
    }
    .items-table thead th {
      padding: 14px 18px;
      text-align: left;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #fff;
    }
    .items-table thead th:last-child { text-align: right; }
    .items-table tbody tr { border-bottom: 1px solid #edf2f7; }
    .items-table tbody tr:last-child { border-bottom: none; }
    .items-table tbody td {
      padding: 18px 18px;
      font-size: 14px;
      color: #4a5568;
      vertical-align: top;
    }
    .items-table tbody td:last-child {
      text-align: right;
      font-weight: 700;
      color: #2d3748;
      font-size: 15px;
    }
    .item-name {
      font-weight: 700;
      color: #2d3748;
      font-size: 15px;
      margin-bottom: 3px;
    }
    .item-desc {
      font-size: 12px;
      color: #a0aec0;
    }

    /* ── Totals ── */
    .totals-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 40px;
    }
    .totals-box {
      min-width: 300px;
      background: #f7fafc;
      border-radius: 10px;
      padding: 20px 24px;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      font-size: 14px;
      color: #718096;
      border-bottom: 1px solid #e2e8f0;
    }
    .totals-row:last-child {
      border-bottom: none;
      padding-top: 14px;
      margin-top: 4px;
    }
    .totals-row.grand {
      font-size: 18px;
      font-weight: 800;
      color: #2d3748;
    }
    .totals-row .val { font-weight: 600; color: #2d3748; }
    .totals-row.grand .val { color: #6c5ce7; font-size: 20px; }

    /* ── Notes ── */
    .notes-section {
      background: #f0ebff;
      border-left: 4px solid #6c5ce7;
      border-radius: 0 8px 8px 0;
      padding: 16px 20px;
      margin-bottom: 40px;
    }
    .notes-section h5 {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #6c5ce7;
      margin-bottom: 6px;
    }
    .notes-section p {
      font-size: 13px;
      color: #553c9a;
      line-height: 1.6;
      margin: 0;
    }

    /* ── Footer ── */
    .invoice-footer {
      background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
      padding: 28px 48px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .footer-brand {
      font-size: 22px;
      font-weight: 900;
      color: #fff;
      letter-spacing: -0.5px;
    }
    .footer-brand span {
      font-size: 12px;
      font-weight: 400;
      color: rgba(255,255,255,0.5);
      display: block;
      letter-spacing: 0;
      margin-top: 2px;
    }
    .footer-contact {
      text-align: right;
      font-size: 12px;
      color: rgba(255,255,255,0.6);
      line-height: 1.8;
    }
    .footer-contact a { color: rgba(255,255,255,0.8); text-decoration: none; }

    /* ── Print styles ── */
    @media print {
      body { background: #fff; padding: 0; }
      .print-actions { display: none !important; }
      .invoice {
        box-shadow: none;
        border-radius: 0;
        max-width: 100%;
      }
      .invoice-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .invoice-footer { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .items-table thead tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .status-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .notes-section { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <!-- Print / Close buttons -->
  <div class="print-actions">
    <button class="btn-close-win" onclick="window.close()">✕ Close</button>
    <button class="btn-print" onclick="window.print()">🖨️ Print / Save as PDF</button>
  </div>

  <div class="invoice">

    <!-- Header -->
    <div class="invoice-header">
      <div class="brand-block">
        <div class="brand-name">TEVE</div>
        <div class="brand-tagline">Professional Streaming Platform</div>
      </div>
      <div class="invoice-title-block">
        <div class="invoice-word">INVOICE</div>
        <div class="invoice-num">${invoiceNumber}</div>
        <div class="invoice-date">Date: ${invoiceDate}</div>
      </div>
    </div>

    <!-- Status strip -->
    <div class="status-strip">
      <span>Payment Status:</span>
      <span class="status-badge" style="background:${statusInfo.bg}; color:${statusInfo.color};">
        ${statusInfo.label}
      </span>
    </div>

    <!-- Body -->
    <div class="invoice-body">

      <!-- Bill To / From -->
      <div class="info-grid">
        <div class="info-block">
          <h4>Bill To</h4>
          <div class="name">${user?.username || 'Customer'}</div>
          <p>${user?.email || ''}</p>
          ${user?.company_name ? `<p>${user.company_name}</p>` : ''}
        </div>
        <div class="info-block">
          <h4>From</h4>
          <div class="name">Siman Tech</div>
          <p>billing@siman.ca</p>
          <p>siman.ca</p>
          <p>GST/HST: 79958 5641 RT0001</p>
          <p style="margin-top:4px;color:#888;">Billed on behalf of Teve</p>
        </div>
      </div>

      <!-- Items -->
      <table class="items-table">
        <thead>
          <tr>
            <th style="width:45%">Description</th>
            <th>Plan</th>
            <th>Billing Date</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div class="item-name">${description}</div>
              <div class="item-desc">Subscription — ${planName}</div>
            </td>
            <td>${planName}</td>
            <td>${invoiceDate}</td>
            <td>${amount}</td>
          </tr>
        </tbody>
      </table>

      <!-- Totals -->
      <div class="totals-wrapper">
        <div class="totals-box">
          <div class="totals-row">
            <span>Subtotal</span>
            <span class="val">${amount}</span>
          </div>
          <div class="totals-row">
            <span>Tax (0%)</span>
            <span class="val">$0.00</span>
          </div>
          <div class="totals-row grand">
            <span>Total</span>
            <span class="val">${amount}</span>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="notes-section">
        <h5>Payment Note</h5>
        <p>
          Thank you for your subscription to Teve. This invoice serves as your official receipt,
          issued by Siman Tech. For any billing questions, contact <strong>billing@siman.ca</strong>.
          Invoice ID: <strong>${invoiceNumber}</strong>
        </p>
      </div>

    </div><!-- /invoice-body -->

    <!-- Footer -->
    <div class="invoice-footer">
      <div class="footer-brand">
        TEVE
        <span>Professional Streaming Platform</span>
      </div>
      <div class="footer-contact">
        <div>billing@siman.ca</div>
        <div><a href="https://siman.ca">siman.ca</a></div>
        <div style="margin-top:6px; color:rgba(255,255,255,0.35); font-size:11px;">
          © ${new Date().getFullYear()} Siman Tech. Invoiced on behalf of Teve.
        </div>
      </div>
    </div>

  </div><!-- /invoice -->

</body>
</html>`;

  const invoiceWindow = window.open('', '_blank', 'width=960,height=800,scrollbars=yes');
  if (invoiceWindow) {
    invoiceWindow.document.write(html);
    invoiceWindow.document.close();
    invoiceWindow.focus();
  } else {
    alert('Please allow pop-ups for this site to download invoices.');
  }
};
